import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Share as CapacitorShare } from '@capacitor/share';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import { API_URL, getFullImageUrl } from '../services/api';
import Toast from '../components/Toast';
import './ProductDetails.css';

const FALLBACK_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"%3E%3Crect width="300" height="300" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';

interface VariationSize {
  ukSize: number;
  footLengthCm: number;
  stockQuantity: number;
}

interface Variation {
  color: string;
  colorCode: string;
  images: string[];
  additionalPrice: number;
  isDefault?: boolean;
  sizes?: VariationSize[];
  stockQuantity?: number;
}

interface UKShoeSize {
  ukSize: number;
  footLengthCm: number;
  stockQuantity: number;
}

interface Dimension {
  length?: number;
  breadth?: number;
  height?: number;
  unit: 'cm' | 'm' | 'inch';
}

interface AgeRange {
  min: number;
  max: number;
}

interface Product {
  _id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  images?: string[];
  variations?: Variation[];
  category: string;
  inStock: boolean;
  stockQuantity: number;
  isFeatured?: boolean;
  discount?: number;
  isOverseas?: boolean;
  ukShoeSizes?: UKShoeSize[];
  dimension?: Dimension;
  rimSize?: string;
  ageRange?: AgeRange;
  relatedCategories?: string[];
}

const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  useAuth();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariation, setSelectedVariation] = useState<Variation | null>(null);
  const [selectedImage, setSelectedImage] = useState('');
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [selectedShoeSize, setSelectedShoeSize] = useState<UKShoeSize | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  const fetchProduct = useCallback(async () => {
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/products/details/${id}`);

      if (!response.ok) {
        if (response.status === 404) {
          navigate('/products');
          return;
        }

        throw new Error(`Product request failed: ${response.status}`);
      }

      const data = await response.json();
      const foundProduct: Product | undefined = data.product;

      if (!foundProduct) {
        navigate('/products');
        return;
      }

      console.log('🔍 Found product:', foundProduct);
      setProduct(foundProduct);

      const defaultVar =
        foundProduct.variations && foundProduct.variations.length > 0
          ? foundProduct.variations[0]
          : null;

      if (defaultVar) {
        setSelectedVariation(defaultVar);
        setSelectedImage(defaultVar.images?.[0] || foundProduct.image);

        if (
          foundProduct.category === 'shoes' &&
          defaultVar.sizes &&
          defaultVar.sizes.length > 0
        ) {
          const firstInStock = defaultVar.sizes.find(
            (size: VariationSize) => size.stockQuantity > 0
          );

          const selectedSize = firstInStock || defaultVar.sizes[0];

          if (selectedSize) {
            setSelectedShoeSize({
              ukSize: selectedSize.ukSize,
              footLengthCm: selectedSize.footLengthCm,
              stockQuantity: selectedSize.stockQuantity
            });
          }
        }
      } else {
        setSelectedVariation(null);
        setSelectedImage(foundProduct.image);
      }

      const relatedProductsList: Product[] =
        Array.isArray(data.relatedProducts) ? data.relatedProducts : [];

      const shuffled = [...relatedProductsList].sort(
        () => 0.5 - Math.random()
      );

      setRelatedProducts(shuffled.slice(0, 8));

    } catch (error) {
      console.error('Failed to fetch product:', error);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchProduct();
    window.scrollTo(0, 0);
  }, [fetchProduct]);
  // Dynamic SEO metadata for the currently displayed product.
  useEffect(() => {
    if (!product) return;

    const originalTitle = document.title;
    const changes: Array<{
      element: Element;
      attribute: string;
      previous: string | null;
      created: boolean;
    }> = [];

    const setHeadAttribute = (
      selector: string,
      attribute: string,
      key: string,
      value: string
    ) => {
      let element = document.head.querySelector(selector);
      const created = !element;

      if (!element) {
        element = document.createElement(
          selector.startsWith('link') ? 'link' : 'meta'
        );
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }

      changes.push({
        element,
        attribute: attribute === 'rel' ? 'href' : 'content',
        previous: element.getAttribute(attribute === 'rel' ? 'href' : 'content'),
        created
      });

      element.setAttribute(attribute === 'rel' ? 'href' : 'content', value);
    };

    const title = `${product.name} | Gimbiya Value Store`;
    const description = (
      product.description ||
      `Shop ${product.name} at Gimbiya Value Store.`
    ).replace(/\s+/g, ' ').trim().slice(0, 300);

    const canonicalUrl = `https://gimbiyastore.com/product/${encodeURIComponent(product._id)}`;
    const imageUrl = getFullImageUrl(product.image);

    document.title = title;

    setHeadAttribute('meta[name="description"]', 'name', 'description', description);
    setHeadAttribute('meta[property="og:type"]', 'property', 'og:type', 'product');
    setHeadAttribute('meta[property="og:title"]', 'property', 'og:title', title);
    setHeadAttribute('meta[property="og:description"]', 'property', 'og:description', description);
    setHeadAttribute('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    setHeadAttribute('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setHeadAttribute('meta[name="twitter:description"]', 'name', 'twitter:description', description);

    if (imageUrl && !imageUrl.startsWith('data:image')) {
      setHeadAttribute('meta[property="og:image"]', 'property', 'og:image', imageUrl);
      setHeadAttribute('meta[name="twitter:image"]', 'name', 'twitter:image', imageUrl);
    }

    setHeadAttribute('link[rel="canonical"]', 'rel', 'canonical', canonicalUrl);

    const structuredData: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description,
      category: product.category,
      url: canonicalUrl,
      offers: {
        '@type': 'Offer',
        priceCurrency: 'NGN',
        price: product.price,
        url: canonicalUrl
      }
    };

    if (imageUrl && !imageUrl.startsWith('data:image')) {
      structuredData.image = [imageUrl];
    }

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.setAttribute('data-gvs-product-schema', 'true');
    schemaScript.textContent = JSON.stringify(structuredData);
    document.head.appendChild(schemaScript);

    return () => {
      schemaScript.remove();

      changes.reverse().forEach(({ element, attribute, previous, created }) => {
        if (created) {
          element.remove();
        } else if (previous === null) {
          element.removeAttribute(attribute);
        } else {
          element.setAttribute(attribute, previous);
        }
      });

      document.title = originalTitle;
    };
  }, [product]);

  const handleVariationSelect = (variation: Variation) => {
    setSelectedVariation(variation);
    const newImage = variation.images && variation.images.length > 0
      ? variation.images[0]
      : product?.image || '';
    setSelectedImage(newImage);
    setQuantity(1);
    
    // Reset selected size for the new color variation
    if (product?.category === 'shoes' && variation.sizes && variation.sizes.length > 0) {
      const firstInStock = variation.sizes.find(s => s.stockQuantity > 0);
      if (firstInStock) {
        setSelectedShoeSize({
          ukSize: firstInStock.ukSize,
          footLengthCm: firstInStock.footLengthCm,
          stockQuantity: firstInStock.stockQuantity
        });
      } else if (variation.sizes[0]) {
        setSelectedShoeSize({
          ukSize: variation.sizes[0].ukSize,
          footLengthCm: variation.sizes[0].footLengthCm,
          stockQuantity: variation.sizes[0].stockQuantity
        });
      } else {
        setSelectedShoeSize(null);
      }
    } else {
      setSelectedShoeSize(null);
    }
  };

  const handleWishlistToggle = () => {
    if (!product) return;
    
    const productId = product._id;
    
    if (isInWishlist(productId)) {
      removeFromWishlist(productId);
      setToast({ 
        message: `Removed from wishlist`, 
        type: 'info' 
      });
    } else {
      addToWishlist({
        _id: productId,
        name: product.name,
        price: product.price,
        image: product.image,
        category: product.category
      })
      setToast({ 
        message: `Added to wishlist`, 
        type: 'success' 
      });
    }
  };

  const handleShareProduct = async () => {
    if (!product) return;

    /*
    * This is the PUBLIC CUSTOMER-FACING URL.
    *
    * Do NOT use the Render API/share endpoint here.
    */
    const storefrontUrl = (
      process.env.REACT_APP_FRONTEND_URL ||
      'https://www.gimbiyastore.com'
    ).replace(/\/+$/, '');

    const shareUrl = `${storefrontUrl}/product/${product._id}`;

    const shareData = {
      title: product.name,
      text: `Check out ${product.name} at Gimbiya Value Store`,
      url: shareUrl,
    };

    try {
      if (Capacitor.isNativePlatform()) {
        await CapacitorShare.share({
          ...shareData,
          dialogTitle: 'Share product',
        });
      } else if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);

        setToast({
          message: 'Product link copied',
          type: 'success',
        });
      } else {
        window.prompt(
          'Copy this product link',
          shareUrl
        );
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error(
          'Failed to share product:',
          error
        );

        setToast({
          message: 'Could not share product link',
          type: 'error',
        });
      }
    }
  };

  // Get current stock based on selection
  const getCurrentStock = (): number => {
    if (!product) return 0;
    
    // For shoe products with size selection
    if (product.category === 'shoes' && selectedShoeSize) {
      return selectedShoeSize.stockQuantity;
    }
    
    // For shoe products with color variations that have sizes
    if (product.category === 'shoes' && selectedVariation && selectedVariation.sizes) {
      if (selectedShoeSize) {
        const sizeStock = selectedVariation.sizes.find(s => s.ukSize === selectedShoeSize.ukSize);
        return sizeStock?.stockQuantity || 0;
      }
      return selectedVariation.sizes.reduce((sum, s) => sum + s.stockQuantity, 0);
    }
    
    // For products with color variations (no sizes)
    if (selectedVariation) {
      return selectedVariation.stockQuantity || 0;
    }
    
    // For simple products
    return product.stockQuantity || 0;
  };

  const handleQuantityChange = (delta: number) => {
    const currentStock = getCurrentStock();
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= currentStock) {
      setQuantity(newQuantity);
    }
  };

  const handleShoeSizeSelect = (size: UKShoeSize) => {
    setSelectedShoeSize(size);
    setQuantity(1);
  };

  const generateCartItemId = (): string => {
    if (!product) return '';
    let uniqueId = product._id;
    if (selectedVariation) {
      uniqueId += `-color-${selectedVariation.color.replace(/\s/g, '')}`;
    }
    if (selectedShoeSize) {
      uniqueId += `-size-${selectedShoeSize.ukSize}`;
    }
    return uniqueId;
  };

  // SINGLE handleAddToCart function (removed duplicate)
  const handleAddToCart = () => {
    if (!product) return;
    
    // Validate size selection for shoes
    if (product.category === 'shoes') {
      if (!selectedShoeSize) {
        alert('Please select a shoe size');
        return;
      }
      if (selectedShoeSize.stockQuantity === 0) {
        alert('This size is out of stock');
        return;
      }
      if (quantity > selectedShoeSize.stockQuantity) {
        alert(`Only ${selectedShoeSize.stockQuantity} units available for this size`);
        return;
      }
    }
    
    // Validate variation selection for color-only products
    if (selectedVariation && selectedVariation.stockQuantity === 0 && !selectedVariation.sizes) {
      alert('This color is out of stock');
      return;
    }
    
    let finalPrice = product.price;
    if (selectedVariation?.additionalPrice) {
      finalPrice += selectedVariation.additionalPrice;
    }
    
    let displayName = product.name;
    if (selectedVariation) {
      displayName = `${product.name} (${selectedVariation.color})`;
    }
    if (selectedShoeSize && product.category === 'shoes') {
      displayName = `${displayName} - UK ${selectedShoeSize.ukSize} (${selectedShoeSize.footLengthCm}cm)`;
    }
    
    const cartItemUniqueId = generateCartItemId();
    const cartItem = {
      cartItemId: cartItemUniqueId,
      productId: product._id,
      id: cartItemUniqueId,
      name: displayName,
      price: finalPrice,
      image: selectedImage,
      quantity: quantity,
      category: product.category,
      isOverseas: product.isOverseas || false,
      selectedColor: selectedVariation?.color,
      selectedSize: selectedShoeSize ? `UK ${selectedShoeSize.ukSize}` : undefined,
      selectedSizeCm: selectedShoeSize?.footLengthCm,
      sizeStockUsed: selectedShoeSize?.stockQuantity,
      variationId: selectedVariation?.color,
      _id: product._id
    };

    addToCart(cartItem);
    alert(`Added ${quantity} × ${displayName} to cart!`);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/cart');
  };

  const calculateDiscountedPrice = () => {
    if (!product) return 0;
    let basePrice = product.price;
    if (selectedVariation?.additionalPrice) {
      basePrice += selectedVariation.additionalPrice;
    }
    if (product.discount && product.discount > 0) {
      return basePrice * (1 - product.discount / 100);
    }
    return basePrice;
  };

  const handleCategoryClick = (category: string) => {
    navigate(`/products?category=${encodeURIComponent(category)}`);
  };

  const getCategoryDisplayName = (category: string) => {
    const categoryNames: { [key: string]: string } = {
      'shoes': 'Shoes',
      'bags': 'Bags',
      'handbags': 'Handbags',
      'travel': 'Travel',
      'home': 'Home',
      'kitchen': 'Kitchen',
      'school': 'School',
      'clothing': 'Clothing',
      'accessories': 'Accessories',
      'wellness': 'Wellness'
    };
    return categoryNames[category] || category.charAt(0).toUpperCase() + category.slice(1);
  };

  const getDimensionDisplay = () => {
    if (!product?.dimension) return null;
    const { length, breadth, height, unit } = product.dimension;
    const parts = [];
    if (length) parts.push(`Length: ${length}${unit}`);
    if (breadth) parts.push(`Breadth: ${breadth}${unit}`);
    if (height) parts.push(`Height: ${height}${unit}`);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const getAgeRangeDisplay = () => {
    if (!product?.ageRange) return null;
    const { min, max } = product.ageRange;
    if (min === 0 && max === 100) return null;
    if (min === max) return `${min}+ years`;
    return `${min}-${max} years`;
  };

  if (loading) {
    return (
      <div className="product-details-loading">
        <div className="spinner"></div>
        <p>Loading product details...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-not-found">
        <h2>Product Not Found</h2>
        <p>The product you're looking for doesn't exist.</p>
        <button onClick={() => navigate('/products')}>Back to Products</button>
      </div>
    );
  }

  const discountedPrice = calculateDiscountedPrice();
  const originalPrice = selectedVariation?.additionalPrice 
    ? product.price + selectedVariation.additionalPrice 
    : product.price;
  const hasDiscount = product.discount && product.discount > 0;
  const currentStock = getCurrentStock();
  const isInStock = currentStock > 0;
  const allVariations = product.variations || [];
  const dimensionDisplay = getDimensionDisplay();
  const ageRangeDisplay = getAgeRangeDisplay();
  const isShoeProduct = product.category === 'shoes';
  const isTravelProduct = product.category === 'travel';
  

  return (
    <div className="product-details-page">
      <div className="product-details-container">        
        <div className="breadcrumb">
          <span onClick={() => navigate('/')}>Home</span>
          <span>/</span>
          <span onClick={() => navigate('/products')}>Products</span>
          <span>/</span>
          <span onClick={() => handleCategoryClick(product.category)}>
            {getCategoryDisplayName(product.category)}
          </span>
          <span>/</span>
          <span>{product.name}</span>
        </div> 

        <div className="product-details-content">
          <div className="product-gallery">
            <div className="main-image">
              <img 
                src={getFullImageUrl(selectedImage)} 
                alt={product.name}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
                }}
              />
              {hasDiscount ? (
                <span className="discount-badge-large">-{product.discount}% OFF</span>
              ) : null}
            </div>
            {/* Thumbnail Gallery - Show thumbnails for ALL color variations */}
            {(allVariations.length > 0 || (product.images && product.images.length > 0)) && (
              <div className="thumbnail-list">
                {/* Show thumbnails for each color variation */}
                {allVariations.map((variation, idx) => {
                  const firstImage =
                    variation.images &&
                    variation.images.length > 0
                      ? variation.images[0]
                      : product.image;

                  // Highlight this thumbnail only when its image
                  // is currently displayed in the main image.
                  const isSelected =
                    selectedImage === firstImage;

                  const isOutOfStock = variation.sizes
                    ? variation.sizes.every(
                        (size) => size.stockQuantity === 0
                      )
                    : (variation.stockQuantity || 0) === 0;

                  return (
                    <div
                      key={`thumb-${variation.color}-${idx}`}
                      className={`thumbnail ${
                        isSelected ? 'active' : ''
                      } ${
                        isOutOfStock ? 'out-of-stock' : ''
                      }`}
                      onClick={() =>
                        !isOutOfStock &&
                        handleVariationSelect(variation)
                      }
                      title={
                        isOutOfStock
                          ? `${variation.color} - Out of Stock`
                          : variation.color
                      }
                    >
                      <img
                        src={getFullImageUrl(firstImage)}
                        alt={`${variation.color} color`}
                        onError={(e) => {
                          const image =
                            e.currentTarget;

                          if (
                            image.dataset.fallbackApplied !== 'true'
                          ) {
                            image.dataset.fallbackApplied = 'true';
                            image.src = FALLBACK_IMAGE;
                          }
                        }}
                      />

                      {isOutOfStock && (
                        <div className="thumbnail-out-of-stock-overlay">
                          Out of Stock
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* If there are additional images for the selected variation, show them too */}
                {selectedVariation?.images &&
                  selectedVariation.images.length > 1 && (
                    <>
                      {selectedVariation.images
                        .slice(1)
                        .map((img, idx) => (
                          <div
                            key={`thumb-extra-${idx}`}
                            className={`thumbnail ${
                              selectedImage === img
                                ? 'active'
                                : ''
                            }`}
                            onClick={() =>
                              setSelectedImage(img)
                            }
                          >
                            <img
                              src={getFullImageUrl(img)}
                              alt={`View ${idx + 2}`}
                              onError={(e) => {
                                const image =
                                  e.currentTarget;

                                if (
                                  image.dataset.fallbackApplied !==
                                  'true'
                                ) {
                                  image.dataset.fallbackApplied =
                                    'true';

                                  image.src = FALLBACK_IMAGE;
                                }
                              }}
                            />
                          </div>
                        ))}
                    </>
                  )}
              </div>
            )}
            
          </div>

          <div className="product-info-section">
            <div className="product-heading-row">
              <h1 className="product-title">{product.name}</h1>
              <button className="share-product-btn" type="button" onClick={handleShareProduct} aria-label="Share product">
                <svg className="share-product-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <path d="m8.7 10.7 6.6-4.4M8.7 13.3l6.6 4.4" />
                </svg>
                <span>Share</span>
              </button>
            </div>
            
            <div className="product-meta">
              <span 
                className="product-category" 
                onClick={() => handleCategoryClick(product.category)}
              >
                {getCategoryDisplayName(product.category)}
              </span>
              <span className={`product-stock ${isInStock ? 'in-stock' : 'out-of-stock'}`}>
                {isInStock ? `✓ In Stock (${currentStock} available)` : '✗ Out of Stock'}
              </span>
            </div>

            {/* Color Variations */}
            {allVariations.length > 0 && (
              <div className="variations-section-details">
                <label>Color:</label>
                <div className="color-options">
                  {allVariations.map((variation, idx) => {
                    const isSelected = selectedVariation?.color === variation.color;
                    let totalStock = 0;
                    let hasSizes = false;
                    
                    if (variation.sizes && variation.sizes.length > 0) {
                      hasSizes = true;
                      totalStock = variation.sizes.reduce((sum, s) => sum + s.stockQuantity, 0);
                    } else {
                      totalStock = variation.stockQuantity || 0;
                    }
                    
                    const isDisabled = totalStock === 0;
                    
                    return (
                      <button
                        key={`${variation.color}-${idx}`}
                        className={`color-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                        onClick={() => !isDisabled && handleVariationSelect(variation)}
                        disabled={isDisabled}
                        title={isDisabled ? 'Out of stock' : `${totalStock} item(s) in stock`}
                      >
                        <span 
                          className="color-swatch-details"
                          style={{ backgroundColor: variation.colorCode }}
                        />
                        <span className="color-name">{variation.color}</span>
                        {variation.additionalPrice > 0 && (
                          <span className="color-price">+₦{variation.additionalPrice.toLocaleString()}</span>
                        )}
                        {hasSizes && variation.sizes && (
                          <span className="color-sizes-count">{variation.sizes.length} sizes</span>
                        )}
                        {isDisabled && <span className="out-of-stock-badge">Out of Stock</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Wishlist Button - Moved outside color variations */}
            <div className="wishlist-section">
              <button 
                className="wishlist-btn-details"
                onClick={handleWishlistToggle}
              >
                {isInWishlist(product._id) ? '❤️ Remove from Wishlist' : '🤍 Add to Wishlist'}
              </button>
            </div>

            {/* Shoe Sizes */}
            {isShoeProduct && (
              <div className="shoe-sizes-section">
                <div className="size-header">
                  <label>UK Size:</label>
                  {selectedShoeSize && (
                    <span className="selected-foot-length">
                      Foot length: {selectedShoeSize.footLengthCm}cm
                    </span>
                  )}
                  <span className="size-warning" title="Size charts are approximate">
                    ⓘ Sizes are approximate
                  </span>
                </div>
                
                {/* Extract sizes from the selected color variation */}
                {selectedVariation && selectedVariation.sizes && selectedVariation.sizes.length > 0 ? (
                  <>
                    <div className="size-chips">
                      {selectedVariation.sizes.map((size) => {
                        const isSelected = selectedShoeSize?.ukSize === size.ukSize;
                        const isDisabled = size.stockQuantity === 0;
                        return (
                          <button
                            key={size.ukSize}
                            className={`size-chip ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                            onClick={() => !isDisabled && handleShoeSizeSelect({
                              ukSize: size.ukSize,
                              footLengthCm: size.footLengthCm,
                              stockQuantity: size.stockQuantity
                            })}
                            disabled={isDisabled}
                            title={isDisabled ? 'Out of stock' : `UK ${size.ukSize} - ${size.footLengthCm}cm (${size.stockQuantity} available)`}
                          >
                            {size.ukSize}
                          </button>
                        );
                      })}
                    </div>
                    
                    {selectedVariation.sizes.every(size => size.stockQuantity === 0) && (
                      <div className="no-sizes-message">
                        <p>⚠️ All sizes for {selectedVariation.color} are currently out of stock</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-sizes-message">
                    <p>⚠️ No sizes have been added for this shoe product.</p>
                  </div>
                )}
              </div>
            )}
 
            {/* Dimension Display */}
            {dimensionDisplay && (
              <div className="product-dimension">
                <span className="dimension-icon">📏</span>
                <span className="dimension-text">Dimensions: {dimensionDisplay}</span>
              </div>
            )}

            {/* Rim Size */}
            {isTravelProduct && product.rimSize && (
              <div className="product-rim-size">
                <span className="rim-icon">🚗</span>
                <span className="rim-text">Rim Size: {product.rimSize}</span>
              </div>
            )}

            {/* Age Range */}
            {ageRangeDisplay && (
              <div className="product-age-range">
                <span className="age-icon">🎂</span>
                <span className="age-text">Recommended Age: {ageRangeDisplay}</span>
              </div>
            )}

            <div className="product-price-section">
              {hasDiscount ? (
                <>
                  <span className="original-price">₦{originalPrice.toLocaleString()}</span>
                  <span className="discounted-price">₦{discountedPrice.toLocaleString()}</span>
                  <span className="savings">Save ₦{(originalPrice - discountedPrice).toLocaleString()}</span>
                </>
              ) : selectedVariation?.additionalPrice ? (
                <>
                  <span className="original-price">₦{product.price.toLocaleString()}</span>
                  <span className="discounted-price">₦{originalPrice.toLocaleString()}</span>
                </>
              ) : (
                <span className="product-price">₦{originalPrice.toLocaleString()}</span>
              )}
            </div>

            <div className="product-description">
              <h3>Product Description</h3>
              <p>{product.description}</p>
            </div>

            {isInStock ? (
              <>
                <div className="quantity-selector">
                  <label>Quantity:</label>
                  <div className="quantity-controls">
                    <button 
                      onClick={() => handleQuantityChange(-1)} 
                      disabled={quantity <= 1}
                      className="quantity-btn"
                    >
                      -
                    </button>
                    <span className="quantity-value">{quantity}</span>
                    <button 
                      onClick={() => handleQuantityChange(1)} 
                      disabled={quantity >= currentStock}
                      className="quantity-btn"
                    >
                      +
                    </button>
                  </div>
                  <span className="stock-info">
                    {currentStock} items available
                  </span>
                </div>
                <div className="action-buttons-details">
                  <button className="add-to-cart-btn-details" onClick={handleAddToCart}>
                    🛒 Add to Cart
                  </button>
                  <button className="buy-now-btn" onClick={handleBuyNow}>
                    Buy Now
                  </button>
                </div>
              </>
            ) : (
              <div className="out-of-stock-message">
                <p>⚠️ This product is currently out of stock.</p>
                {isShoeProduct && selectedVariation?.sizes?.some(size => size.stockQuantity === 0) && (
                  <p className="restock-note">Some sizes may be restocked soon. Check back later!</p>
                )}
              </div>
            )}

            <div className="product-features">
              <div className="feature">
                <span className="feature-icon">🚚</span>
                <div>
                  <strong>Free Shipping</strong>
                  <small> On all eligible orders</small>
                </div>
              </div>
              <div className="feature">
                <span className="feature-icon">🔄</span>
                <div>
                  <strong>2-3 Days Returns</strong>
                  <small>Easy returns policy</small>
                </div>
              </div>
              <div className="feature">
                <span className="feature-icon">🔒</span>
                <div>
                  <strong>Secure Payment</strong>
                  <small>100% secure transactions</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <div className="related-products">
            <h2>You May Also Like</h2>
            <div className="related-products-grid">
              {relatedProducts.map(related => {
                // Check if the related product is in stock
                const relatedInStock = related.inStock && (related.stockQuantity || 0) > 0;
                
                return (
                  <div 
                    key={related._id} 
                    className={`related-product-card ${!relatedInStock ? 'out-of-stock' : ''}`}
                    onClick={() => {
                      // Only navigate if in stock, or if you want to allow viewing out-of-stock items
                      navigate(`/product/${related._id}`);
                    }}
                  >
                    <img 
                      src={getFullImageUrl(related.image)} 
                      alt={related.name}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
                      }}
                    />
                    <p className="related-product-name">{related.name}</p>
                    <p className="related-product-price">₦{related.price.toLocaleString()}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

       {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ProductDetails;
